import React, { useState } from 'react';
import { CalendarDotsIcon } from '@phosphor-icons/react';
import { DateTimeRangePicker, Tag } from '@/components';
import type { DateTimeRangePickerResult } from '@/components/DateTime/DateTimeRangePicker';
import { useRetentionStartDate } from '@/hooks';
import { useQueryFiltersStore } from '@/stores/query-filters.store';
import {
    DateTimeMode,
    getDateRangeFromMode,
    getDateTimeLabel,
    type DateTimeFilter,
} from '@/utils/query-filters';
import { ChipPopover } from './ChipPopover';

interface DateTimeFilterChipProps {
    onApply: (range: {
        startTime: Date;
        endTime: Date;
        mode: DateTimeMode;
    }) => void;
    dateTimeOverride?: DateTimeFilter;
    tagVariant?: 'base' | 'plain';
}

export function DateTimeFilterChip({
    onApply,
    dateTimeOverride,
    tagVariant = 'base',
}: DateTimeFilterChipProps) {
    const store = useQueryFiltersStore();
    const retentionStart = useRetentionStartDate();
    const [popoverOpen, setPopoverOpen] = useState(false);

    const dateTime = dateTimeOverride ?? store.filters.dateTime;

    const dateLabel = getDateTimeLabel(dateTime);
    const resolvedRange =
        dateTime.mode === DateTimeMode.CUSTOM
            ? dateTime.customRange
            : getDateRangeFromMode(dateTime.mode);

    return (
        <ChipPopover
            popoverOpen={popoverOpen}
            onPopoverOpenChange={setPopoverOpen}
            chip={
                <Tag
                    data-testid="filter-chip-date-time"
                    variant={tagVariant}
                    size="sm"
                    leadingIcon={<CalendarDotsIcon weight="fill" />}
                    onClick={() => setPopoverOpen(!popoverOpen)}
                >
                    {dateLabel}
                </Tag>
            }
        >
            <DateTimeRangePicker
                initialStartDate={resolvedRange?.startDate}
                initialEndDate={resolvedRange?.endDate}
                initialMode={dateTime.mode}
                earliestDate={retentionStart}
                immediatelyApply
                onApply={(range: DateTimeRangePickerResult) => {
                    if (range.startTime && range.endTime) {
                        onApply({
                            startTime: range.startTime,
                            endTime: range.endTime,
                            mode: range.mode,
                        });
                    }
                }}
                onClose={() => setPopoverOpen(false)}
            />
        </ChipPopover>
    );
}
